import { ClipboardX, Activity, Wrench, CreditCard } from "lucide-react";
import { Kicker, MaskLines, Rise, SlideShell } from "@/deck/ui";

const problems = [
  { icon: ClipboardX, title: "Manual & opaque", text: "Utility readings by hand — inaccurate, untraceable, disputed.", img: "/assets/gal-4.png" },
  { icon: Activity, title: "No real-time view", text: "Building Management can't monitor usage or bill tenants live." },
  { icon: Wrench, title: "Technician-dependent", text: "Every top-up or meter glitch waits for technical help.", img: "/assets/gal-2.png" },
  { icon: CreditCard, title: "Payment friction", text: "Tenants need simpler ways to transact and pay." },
];

const Card = ({ p, i }) => (
  <Rise delay={0.35 + i * 0.1} className="group relative overflow-hidden rounded-3xl border border-white/10 bg-[var(--panel)] p-5 lg:p-6" data-testid={`problem-card-${i + 1}`}>
    {p.img && (
      <>
        <img src={p.img} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30 grayscale transition-[opacity,transform] duration-700 group-hover:scale-105 group-hover:opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07080b] via-[#07080b]/70 to-transparent" />
      </>
    )}
    <div className="relative flex h-full flex-col justify-between gap-6">
      <div className="flex items-start justify-between">
        <span className="font-display text-5xl font-extrabold tracking-tighter text-zinc-800 lg:text-6xl" style={{ WebkitTextStroke: "1px #3f3f46" }}>
          0{i + 1}
        </span>
        <span className="grid h-10 w-10 place-items-center rounded-full border border-dash/40 text-[var(--dash)]">
          <p.icon size={18} />
        </span>
      </div>
      <div>
        <h3 className="font-display text-xl font-semibold tracking-tight text-zinc-50 lg:text-2xl">{p.title}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-zinc-300">{p.text}</p>
      </div>
    </div>
  </Rise>
);

export default function Problem() {
  return (
    <SlideShell testId="problem-slide">
      <div className="mb-6 flex flex-col gap-4 lg:mb-8 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Rise><Kicker index="02">The problem</Kicker></Rise>
          <MaskLines lines={["Buildings still run", "on guesswork."]} accentIndex={1} className="mt-4 text-4xl font-extrabold leading-[0.95] tracking-[-0.035em] sm:text-5xl lg:text-6xl" />
        </div>
      </div>
      <div className="grid flex-1 gap-4 lg:min-h-0 lg:grid-cols-12">
        <Rise delay={0.25} className="relative min-h-[260px] overflow-hidden rounded-3xl border border-white/10 lg:col-span-5" data-testid="problem-hero-photo">
          <img src="/assets/gal-5.png" alt="Tenant reading a prepaid meter by candlelight" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_60%_45%,transparent_10%,rgba(7,8,11,0.85)_65%)]" />
          <div className="absolute bottom-0 left-0 p-6">
            <p className="font-mono-d text-[11px] uppercase tracking-[0.22em] text-[var(--dash)]">Reality today</p>
            <p className="mt-2 max-w-xs font-display text-2xl font-semibold leading-tight text-zinc-50">Credit runs out. Lights go out. Nobody saw it coming.</p>
          </div>
        </Rise>
        <div className="grid gap-4 sm:grid-cols-2 lg:col-span-7 lg:grid-rows-2">
          {problems.map((p, i) => <Card key={p.title} p={p} i={i} />)}
        </div>
      </div>
    </SlideShell>
  );
}
